import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { LocalizedApiError } from '@/features/audit-issues/LocalizedApiError';
import type { InboundDeleteBlocker } from './types';
import { useDeleteInboundLot, useInboundLotDeletePreview } from './useInbound';

export interface DeleteInboundLotDialogLot {
  inbound_lot_id: string;
  lot_code?: string | null;
  status?: string | null;
  inbound_date?: string | null;
  linked_dispatch_request_id?: string | null;
}

interface DeleteInboundLotDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lot: DeleteInboundLotDialogLot | null;
  showTechnicalDetails?: boolean;
  onDeleted?: () => void;
}

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const countOrZero = (value: number | undefined) => value ?? 0;

export const DeleteInboundLotDialog = ({
  open,
  onOpenChange,
  lot,
  showTechnicalDetails = false,
  onDeleted,
}: DeleteInboundLotDialogProps) => {
  const { t } = useTranslation('operations');
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const preview = useInboundLotDeletePreview(lot?.inbound_lot_id, open && !!lot);
  const deleteLot = useDeleteInboundLot();

  useEffect(() => {
    if (open) {
      setReason('');
      setReasonError(null);
    }
  }, [open, lot?.inbound_lot_id]);

  const blockers = useMemo<InboundDeleteBlocker[]>(
    () => preview.data?.blockers ?? [],
    [preview.data?.blockers],
  );
  const canCancel = preview.data?.can_cancel ?? preview.data?.can_delete;
  const isDispatchReturn = !!(
    preview.data?.linked_dispatch_request_id ??
    lot?.linked_dispatch_request_id ??
    (preview.data?.return_step === 'dispatch' ? 'dispatch' : null)
  );
  const blocked =
    canCancel === false || blockers.length > 0 || countOrZero(preview.data?.blocker_count) > 0;
  const canSubmit =
    !deleteLot.isPending &&
    !preview.isLoading &&
    !preview.isFetching &&
    !preview.error &&
    !blocked &&
    !!preview.data &&
    reason.trim().length > 0;

  const submit = async () => {
    const trimmed = reason.trim();
    if (!lot || !trimmed) {
      setReasonError(
        t('efs.operations.inbound.lot_delete.reason_required', {
          defaultValue: '削除理由を入力してください。',
        }),
      );
      reasonRef.current?.focus();
      return;
    }
    setReasonError(null);
    try {
      await deleteLot.mutateAsync({
        lotId: lot.inbound_lot_id,
        body: { reason: trimmed },
      });
      onDeleted?.();
      onOpenChange(false);
    } catch {
      // React Query exposes the localized API error below.
    }
  };

  return (
    <Dialog open={open} onOpenChange={deleteLot.isPending ? undefined : onOpenChange}>
      <DialogContent className="max-h-[min(90vh,820px)] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t('efs.operations.inbound.lot_delete.title', {
              defaultValue: '入荷を取り消して前工程へ戻す',
            })}
          </DialogTitle>
          <DialogDescription>
            {lot
              ? t('efs.operations.inbound.lot_delete.description', {
                  code: lot.lot_code || lot.inbound_lot_id,
                  defaultValue:
                    '{{code}} の入荷記録を取り消し、関連データを外して前工程へ戻します。',
                })
              : null}
          </DialogDescription>
        </DialogHeader>

        {lot ? (
          <div className="space-y-1 rounded-md border border-efs-border bg-efs-bg-subtle p-3 text-sm">
            <div className="font-mono text-xs">{lot.lot_code || lot.inbound_lot_id}</div>
            <div className="text-xs text-efs-text-muted">
              {lot.inbound_date ?? '—'} · {lot.status ?? '—'}
            </div>
            {lot.linked_dispatch_request_id ? (
              <div className="text-xs text-amber-900 dark:text-amber-100">
                {t('efs.operations.inbound.lot_delete.dispatch_linked_hint', {
                  defaultValue: '配車から作成された入荷です。取消後、配車は受諾済みに戻ります。',
                })}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
          {preview.isLoading || preview.isFetching ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              {t('efs.operations.inbound.lot_delete.loading_preview', {
                defaultValue: '影響範囲を確認しています…',
              })}
            </span>
          ) : preview.data ? (
            <div className="space-y-3">
              <p className="font-medium">
                {t('efs.operations.inbound.lot_delete.immediate_effect', {
                  context: isDispatchReturn ? 'dispatch' : 'manual',
                  defaultValue:
                    '取消後、この入荷と関連データは通常一覧から外れ、前工程へ戻ります。',
                })}
              </p>
              <p>
                {t('efs.operations.inbound.lot_delete.retention_notice', {
                  purgeAfter: formatDateTime(preview.data.purge_after),
                  defaultValue: '完全削除予定: {{purgeAfter}}',
                })}
              </p>
              <dl className="grid grid-cols-2 gap-2 text-xs">
                {[
                  ['inbound_lots_label', preview.data.inbound_lot_count],
                  ['packages_label', preview.data.inbound_package_count],
                  ['sessions_label', preview.data.dismantling_session_count],
                  ['outputs_label', preview.data.dismantling_output_count],
                  ['movements_label', preview.data.inventory_movement_count],
                ].map(([key, count]) => (
                  <div key={key}>
                    <dt className="text-efs-text-muted">
                      {t(`efs.operations.inbound.lot_delete.${key}`, { defaultValue: String(key) })}
                    </dt>
                    <dd className="font-semibold">
                      {t('efs.operations.inbound.lot_delete.count_unit', {
                        count: count as number,
                        defaultValue: '{{count}} 件',
                      })}
                    </dd>
                  </div>
                ))}
              </dl>
              {blocked ? (
                <div className="rounded border border-destructive/40 bg-destructive/10 p-2 text-destructive">
                  <p className="inline-flex items-center gap-1 font-medium">
                    <AlertTriangle className="h-4 w-4" aria-hidden />
                    {t('efs.operations.inbound.lot_delete.blocked', {
                      defaultValue: '後続データがあるため取消できません。',
                    })}
                  </p>
                  {blockers.length > 0 ? (
                    <ul className="mt-1 list-disc pl-5">
                      {blockers.map((blocker) => (
                        <li key={blocker.code}>
                          {t(`efs.operations.inbound.lot_delete.blockers.${blocker.code}`, {
                            count: blocker.count,
                            defaultValue: `${blocker.code} (${blocker.count})`,
                          })}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : preview.error ? (
            <LocalizedApiError error={preview.error} showTechnicalDetails={showTechnicalDetails} />
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="inbound-lot-delete-reason">
            {t('efs.operations.inbound.lot_delete.reason', {
              defaultValue: '取消理由',
            })}
            <span className="ml-1 text-red-900">*</span>
          </Label>
          <Textarea
            id="inbound-lot-delete-reason"
            ref={reasonRef}
            rows={3}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (reasonError && e.target.value.trim()) setReasonError(null);
            }}
            placeholder={t('efs.operations.inbound.lot_delete.reason_placeholder', {
              defaultValue: '例: 入荷時の入力内容が誤っていたため前工程へ戻す',
            })}
          />
          {reasonError ? <p className="text-sm text-destructive">{reasonError}</p> : null}
          {deleteLot.error ? (
            <LocalizedApiError
              error={deleteLot.error}
              showTechnicalDetails={showTechnicalDetails}
            />
          ) : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={deleteLot.isPending}
          >
            {t('efs.common.actions.cancel', { ns: 'common' })}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => void submit()}
            disabled={!canSubmit}
          >
            {deleteLot.isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <RotateCcw className="mr-1 h-4 w-4" />
            )}
            {t('efs.operations.inbound.lot_delete.confirm', {
              defaultValue: '取消して前工程へ戻す',
            })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteInboundLotDialog;
